// Modal de ações em lote — sub-form variando por action:
// - marcar_pago / alterar_vencimento / alterar_valor / alterar_conta → 1 campo
// - marcar_em_aberto / marcar_agendado / desmarcar_agendado → só Alert
// - editar_campos → multi-campo com toggle por campo (categoria, CC, fornecedor,
//   forma de pagamento, observações).
// Extraído de FinanceiroContasPagar.tsx em 2026-05-23 (frontend wave 3B).
import {
  Alert, DatePicker, Form, Input, Modal, Select, Space, Typography,
} from 'antd';
import dayjs from 'dayjs';
import { AcaoLoteFormState } from './BarraAcoesLote';
import { opcoesFornecedor, filtrarFornecedor } from '../../utils/fornecedores';

const { Text } = Typography;

export function ModalBulkEdit({
  acaoLoteForm, setAcaoLoteForm, qtdSelecionados, aplicarLote, aplicandoLote,
  contasDisponiveis, categoriasOptions, fornecedoresList, centrosCustoOptions,
}: {
  acaoLoteForm: AcaoLoteFormState;
  setAcaoLoteForm: (s: AcaoLoteFormState | null) => void;
  qtdSelecionados: number;
  aplicarLote: () => Promise<void>;
  aplicandoLote: boolean;
  contasDisponiveis: string[];
  categoriasOptions: { label: string; value: string }[];
  fornecedoresList: any[];
  centrosCustoOptions: { label: string; value: string; nome: string }[];
}) {
  return (
    <Modal open onCancel={() => setAcaoLoteForm(null)} centered
      onOk={aplicarLote} confirmLoading={aplicandoLote}
      okText={`Aplicar a ${qtdSelecionados}`} cancelText="Cancelar"
      title={
        acaoLoteForm.action === 'marcar_pago' ? `Definir ${qtdSelecionados} como pago`
        : acaoLoteForm.action === 'marcar_em_aberto' ? `Definir ${qtdSelecionados} como em aberto`
        : acaoLoteForm.action === 'marcar_agendado' ? `Marcar ${qtdSelecionados} como agendado`
        : acaoLoteForm.action === 'desmarcar_agendado' ? `Desmarcar ${qtdSelecionados} como agendado`
        : acaoLoteForm.action === 'alterar_vencimento' ? `Alterar vencimento de ${qtdSelecionados}`
        : acaoLoteForm.action === 'alterar_valor' ? `Alterar valor de ${qtdSelecionados}`
        : acaoLoteForm.action === 'alterar_conta' ? `Alterar conta de ${qtdSelecionados}`
        : acaoLoteForm.action === 'editar_campos' ? `Editar campos de ${qtdSelecionados} lançamentos`
        : 'Aplicar em lote'
      }
      width={acaoLoteForm.action === 'editar_campos' ? 720 : 460} destroyOnClose>
      <Form layout="vertical" size="small">
        {acaoLoteForm.action === 'marcar_pago' && (
          <Form.Item label="Data do pagamento" required>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={acaoLoteForm.payload.data_pagamento ? dayjs(acaoLoteForm.payload.data_pagamento) : null}
              onChange={(d) => setAcaoLoteForm({ ...acaoLoteForm, payload: { ...acaoLoteForm.payload, data_pagamento: d ? d.format('YYYY-MM-DD') : '' } })} />
          </Form.Item>
        )}
        {acaoLoteForm.action === 'alterar_vencimento' && (
          <Form.Item label="Novo vencimento" required>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={acaoLoteForm.payload.data_vencimento ? dayjs(acaoLoteForm.payload.data_vencimento) : null}
              onChange={(d) => setAcaoLoteForm({ ...acaoLoteForm, payload: { data_vencimento: d ? d.format('YYYY-MM-DD') : '' } })} />
          </Form.Item>
        )}
        {acaoLoteForm.action === 'alterar_valor' && (
          <Form.Item label="Novo valor (R$)" required>
            <Input type="number" step="0.01" min={0}
              value={acaoLoteForm.payload.valor || ''}
              onChange={e => setAcaoLoteForm({ ...acaoLoteForm, payload: { valor: e.target.value } })}
              placeholder="0,00" />
            <Text type="secondary" style={{ fontSize: 10 }}>O sinal negativo é aplicado automaticamente (despesa).</Text>
          </Form.Item>
        )}
        {acaoLoteForm.action === 'alterar_conta' && (
          <Form.Item label="Nova conta financeira" required>
            <Select value={acaoLoteForm.payload.conta_financeira_ca || undefined}
              onChange={(v) => setAcaoLoteForm({ ...acaoLoteForm, payload: { conta_financeira_ca: v || '' } })}
              allowClear showSearch placeholder="Selecione…"
              options={contasDisponiveis.map(c => ({ label: c, value: c }))}
              style={{ width: '100%' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>Aplicada só em lançamentos do Conta Azul (manuais não têm conta).</Text>
          </Form.Item>
        )}
        {['marcar_em_aberto', 'marcar_agendado', 'desmarcar_agendado'].includes(acaoLoteForm.action) && (
          <Alert
            type="info" showIcon
            message={`Esta ação alterará o status de ${qtdSelecionados} lançamentos.`}
            description={
              acaoLoteForm.action === 'marcar_em_aberto'
                ? 'A data de pagamento será apagada e o status volta pra "em aberto".'
                : acaoLoteForm.action === 'marcar_agendado'
                ? 'Lançamentos serão marcados como "agendado" (pagamento programado).'
                : 'Lançamentos voltam pra "em aberto".'
            } />
        )}

        {/* F-CP-bulk-edit: form multi-campo com toggle por campo */}
        {acaoLoteForm.action === 'editar_campos' && (() => {
          const p = acaoLoteForm.payload || {};
          const setPayload = (next: any) => setAcaoLoteForm({ ...acaoLoteForm, payload: next });
          // helper: liga/desliga campo
          const togglar = (key: string, valorInicial: any) => {
            const novoPayload = { ...p };
            if (key in novoPayload) delete novoPayload[key];
            else novoPayload[key] = valorInicial;
            setPayload(novoPayload);
          };
          const ativo = (key: string) => key in p;
          const camposEscolhidos = Object.keys(p).length;
          return <>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message={`${camposEscolhidos} campo${camposEscolhidos !== 1 ? 's' : ''} selecionado${camposEscolhidos !== 1 ? 's' : ''} pra atualizar.`}
              description="Marque os campos que você quer alterar. Só os marcados serão atualizados nos lançamentos selecionados." />

            {/* Categoria */}
            <div style={{ marginBottom: 14, padding: 10, border: '0.5px solid #eee', borderRadius: 6,
              background: ativo('categoria_dre') ? '#FAFBFF' : '#fafafa' }}>
              <Form.Item style={{ marginBottom: ativo('categoria_dre') ? 8 : 0 }}>
                <Space>
                  <input type="checkbox" checked={ativo('categoria_dre')}
                    onChange={() => togglar('categoria_dre', '')} />
                  <Text strong style={{ fontSize: 12 }}>🏷️ Categoria</Text>
                </Space>
              </Form.Item>
              {ativo('categoria_dre') && (
                <Select value={p.categoria_dre || undefined}
                  onChange={(v) => setPayload({ ...p, categoria_dre: v || '' })}
                  placeholder="Selecione a categoria…" allowClear showSearch optionFilterProp="label"
                  options={categoriasOptions} style={{ width: '100%' }} />
              )}
            </div>

            {/* Centro de Custo */}
            <div style={{ marginBottom: 14, padding: 10, border: '0.5px solid #eee', borderRadius: 6,
              background: ativo('centros_custo') ? '#FAFBFF' : '#fafafa' }}>
              <Form.Item style={{ marginBottom: ativo('centros_custo') ? 8 : 0 }}>
                <Space>
                  <input type="checkbox" checked={ativo('centros_custo')}
                    onChange={() => togglar('centros_custo', [])} />
                  <Text strong style={{ fontSize: 12 }}>🎯 Centro de Custo</Text>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    (substitui o CC atual — limpa rateio Clockify se houver)
                  </Text>
                </Space>
              </Form.Item>
              {ativo('centros_custo') && (
                <Select value={(p.centros_custo?.[0]?.centro) || undefined}
                  onChange={(v) => {
                    if (!v) { setPayload({ ...p, centros_custo: [] }); return; }
                    const cc = centrosCustoOptions.find(x => x.value === v);
                    setPayload({ ...p, centros_custo: [{
                      centro: v,
                      cc_contrato_numero: v,
                      cc_contrato_titulo: cc?.nome || null,
                      valor: 0,  // backend não usa pro UPDATE; valor virá do total da parcela
                    }]});
                  }}
                  placeholder="Selecione ou deixe vazio pra limpar CC…" allowClear showSearch optionFilterProp="label"
                  options={centrosCustoOptions} style={{ width: '100%' }} />
              )}
            </div>

            {/* Fornecedor */}
            <div style={{ marginBottom: 14, padding: 10, border: '0.5px solid #eee', borderRadius: 6,
              background: ativo('fornecedor_id') ? '#FAFBFF' : '#fafafa' }}>
              <Form.Item style={{ marginBottom: ativo('fornecedor_id') ? 8 : 0 }}>
                <Space>
                  <input type="checkbox" checked={ativo('fornecedor_id')}
                    onChange={() => {
                      if (ativo('fornecedor_id')) {
                        const np = { ...p };
                        delete np.fornecedor_id;
                        delete np.fornecedor_original;
                        setPayload(np);
                      } else {
                        setPayload({ ...p, fornecedor_id: null, fornecedor_original: '' });
                      }
                    }} />
                  <Text strong style={{ fontSize: 12 }}>🏢 Fornecedor</Text>
                </Space>
              </Form.Item>
              {ativo('fornecedor_id') && (
                <Select value={p.fornecedor_id || undefined}
                  onChange={(v) => {
                    const f = fornecedoresList.find((x: any) => x.id === v);
                    setPayload({ ...p, fornecedor_id: v || null,
                      fornecedor_original: f?.nome_fantasia || '' });
                  }}
                  placeholder="Selecione fornecedor…" allowClear showSearch filterOption={filtrarFornecedor}
                  options={opcoesFornecedor(fornecedoresList, { avisarSemCnpj: true })}
                  style={{ width: '100%' }} />
              )}
            </div>

            {/* Forma de pagamento */}
            <div style={{ marginBottom: 14, padding: 10, border: '0.5px solid #eee', borderRadius: 6,
              background: ativo('metodo_pagamento_ca') ? '#FAFBFF' : '#fafafa' }}>
              <Form.Item style={{ marginBottom: ativo('metodo_pagamento_ca') ? 8 : 0 }}>
                <Space>
                  <input type="checkbox" checked={ativo('metodo_pagamento_ca')}
                    onChange={() => togglar('metodo_pagamento_ca', '')} />
                  <Text strong style={{ fontSize: 12 }}>💳 Forma de pagamento</Text>
                </Space>
              </Form.Item>
              {ativo('metodo_pagamento_ca') && (
                <Select value={p.metodo_pagamento_ca || undefined}
                  onChange={(v) => setPayload({ ...p, metodo_pagamento_ca: v || '' })}
                  placeholder="Selecione…" allowClear
                  options={[
                    { label: 'PIX', value: 'PIX' }, { label: 'Boleto', value: 'Boleto' },
                    { label: 'TED', value: 'TED' }, { label: 'Cartão', value: 'Cartão' },
                    { label: 'Dinheiro', value: 'Dinheiro' }, { label: 'Cheque', value: 'Cheque' },
                  ]} style={{ width: '100%' }} />
              )}
            </div>

            {/* Observações */}
            <div style={{ marginBottom: 4, padding: 10, border: '0.5px solid #eee', borderRadius: 6,
              background: ativo('observacoes') ? '#FAFBFF' : '#fafafa' }}>
              <Form.Item style={{ marginBottom: ativo('observacoes') ? 8 : 0 }}>
                <Space>
                  <input type="checkbox" checked={ativo('observacoes')}
                    onChange={() => togglar('observacoes', '')} />
                  <Text strong style={{ fontSize: 12 }}>📝 Observações</Text>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    (substitui texto atual em todos)
                  </Text>
                </Space>
              </Form.Item>
              {ativo('observacoes') && (
                <Input.TextArea value={p.observacoes || ''}
                  onChange={e => setPayload({ ...p, observacoes: e.target.value })}
                  rows={2} placeholder="Texto novo…" />
              )}
            </div>
          </>;
        })()}
      </Form>
    </Modal>
  );
}
